import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  Check,
  ArrowForward,
  ArrowBack,
  Close,
  Wifi,
  AcUnit,
  LocalCafe,
  WaterDrop,
  Power,
  LocalParking,
  Security,
  MenuBook,
} from '@mui/icons-material';

const amenityIcons = {
  wifi: <Wifi />,
  ac: <AcUnit />,
  'air conditioning': <AcUnit />,
  cafeteria: <LocalCafe />,
  cafe: <LocalCafe />,
  water: <WaterDrop />,
  'drinking water': <WaterDrop />,
  power: <Power />,
  'power backup': <Power />,
  parking: <LocalParking />,
  security: <Security />,
  'reading room': <MenuBook />,
  locker: <Security />,
};

const getAmenityIcon = (amenity) => {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || <Check />;
};

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function MinimalTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `A quiet space for focused work and study.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
      {/* Navigation */}
      <Box sx={{ py: 3, borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 300, letterSpacing: 2 }}>
              {library.name}
            </Typography>
            <Button
              onClick={onBookNow}
              sx={{
                color: accentColor,
                fontWeight: 400,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Book
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero - Full width image */}
      <Box sx={{ position: 'relative', height: { xs: '60vh', md: '80vh' } }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}
      </Box>

      {/* Introduction */}
      <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontWeight: 300, mb: 4, letterSpacing: -1 }}>
          {library.name}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', lineHeight: 2, fontSize: '1.1rem' }}
        >
          {library.description || defaultDescription}
        </Typography>
      </Container>

      {/* Key Info */}
      <Box sx={{ borderTop: '1px solid #eee', borderBottom: '1px solid #eee', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                Location
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                {library.address}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                Capacity
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                {library.capacity} seats
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                Hours
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                {library.operating_hours || 'Contact for hours'}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Plans */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, display: 'block', mb: 4 }}>
          Membership
        </Typography>

        {plans.length > 0 ? (
          <Grid container spacing={4}>
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} md={3} key={plan.id}>
                <Box
                  sx={{
                    p: 4,
                    border: '1px solid #eee',
                    textAlign: 'center',
                    transition: 'border-color 0.3s',
                    '&:hover': { borderColor: accentColor },
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    {plan.plan_type.replace('_', ' ')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 300, my: 2 }}>
                    ₹{plan.price_cents}
                  </Typography>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={onBookNow}
                    sx={{ color: accentColor, fontWeight: 400 }}
                  >
                    Select
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 3 }}>
              Contact us for pricing
            </Typography>
            <Button onClick={onBookNow} sx={{ color: accentColor }}>
              Get in touch
            </Button>
          </Box>
        )}
      </Container>

      {/* Gallery */}
      <Box sx={{ py: 12, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            {images.slice(0, 6).map((img, idx) => (
              <Grid item xs={4} key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: { xs: 120, md: 240 },
                    objectFit: 'cover',
                    cursor: 'pointer',
                    transition: 'opacity 0.3s',
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Amenities */}
      {library.amenities?.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, display: 'block', mb: 4 }}>
            Amenities
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {library.amenities.map((amenity, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: accentColor }}>
                  {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 20 } })}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  {amenity}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      )}

      {/* Map & Contact */}
      <Box sx={{ borderTop: '1px solid #eee', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 300, bgcolor: '#f5f5f5' }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'grayscale(100%)' }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                Contact
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body1">{library.address}</Typography>
                {library.contact_phone && (
                  <Typography variant="body1">
                    <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit' }}>
                      {library.contact_phone}
                    </a>
                  </Typography>
                )}
                {library.contact_email && (
                  <Typography variant="body1">
                    <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit' }}>
                      {library.contact_email}
                    </a>
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                onClick={onBookNow}
                sx={{
                  mt: 4,
                  borderColor: 'black',
                  color: 'black',
                  borderRadius: 0,
                  px: 4,
                  '&:hover': { bgcolor: 'black', color: 'white' },
                }}
              >
                Book a Seat
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: '1px solid #eee', textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'none' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default MinimalTheme;
